import { useCallback, useEffect, useRef, type PointerEvent, type RefObject } from 'react';
import { comparisonChrome, splitFromClientX } from './comparison-split';

type Props = {
  value: number;
  onChange: (value: number) => void;
  locale: string;
  artwork: RefObject<HTMLDivElement | null>;
  bounds: { left: number; top: number; width: number; height: number };
  viewport: { width: number; height: number };
};

/** Constant screen-size controls over the same source/vector clipping boundary. */
export function ComparisonDivider({ value, onChange, locale, artwork, bounds, viewport }: Props) {
  const handle = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ id: number; offset: number; startX: number; moved: boolean } | null>(null);
  const chrome = comparisonChrome(bounds, viewport, value);
  const stop = useCallback(() => {
    const operation = drag.current; drag.current = null;
    if (operation && handle.current?.hasPointerCapture(operation.id)) handle.current.releasePointerCapture(operation.id);
  }, []);
  useEffect(() => {
    const visibility = () => { if (document.hidden) stop(); };
    window.addEventListener('blur', stop);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      stop(); window.removeEventListener('blur', stop);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [stop]);
  useEffect(() => { if (!chrome) stop(); }, [Boolean(chrome), stop]);
  const update = (event: PointerEvent<HTMLButtonElement>) => {
    const operation = drag.current; if (!operation || operation.id !== event.pointerId) return;
    // Read the current rect: Alt-wheel or a resize can change it during capture.
    const rect = artwork.current?.getBoundingClientRect();
    if (rect) onChange(splitFromClientX(event.clientX - operation.offset, rect.left, rect.width));
  };
  if (!chrome) return null;
  const t = (vi: string, en: string) => locale === 'en' ? en : vi;
  return <>
    <span className="vectorize-comparison-line" data-testid="vectorize-comparison-line" aria-hidden="true" style={{ left: chrome.lineX, top: chrome.top, height: chrome.height }}/>
    <button ref={handle} className="vectorize-comparison-handle" data-testid="vectorize-comparison-handle" type="button" role="slider" aria-label={t('Vị trí so sánh ảnh gốc và vector', 'Original and vector comparison position')} aria-orientation="horizontal" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Number(value.toFixed(2))} aria-valuetext={`${Math.round(value)}% ${t('ảnh gốc', 'original')} · ${Math.round(100 - value)}% vector`} style={{ left: chrome.handleX, top: chrome.handleY }} title={t('Kéo để so sánh · ← → để điều chỉnh', 'Drag to compare · ← → to adjust')} onPointerDown={event => {
      if (event.button !== 0 || !event.isPrimary || drag.current) return;
      event.preventDefault(); event.stopPropagation(); event.currentTarget.focus({ preventScroll: true });
      const rect = artwork.current?.getBoundingClientRect(); if (!rect) return;
      // Preserve the grip offset; a pinned edge handle can recover an offscreen
      // boundary directly on its first movement.
      drag.current = { id: event.pointerId, offset: chrome.pinned ? 0 : event.clientX - (rect.left + rect.width * value / 100), startX: event.clientX, moved: false };
      event.currentTarget.setPointerCapture(event.pointerId);
    }} onPointerMove={event => {
      if (drag.current?.id !== event.pointerId) return;
      event.stopPropagation(); if (!event.buttons) { stop(); return; }
      if (Math.abs(event.clientX - drag.current.startX) >= 1) drag.current.moved = true;
      if (drag.current.moved) update(event);
    }} onPointerUp={event => {
      if (drag.current?.id !== event.pointerId) return;
      if (drag.current.moved || Math.abs(event.clientX - drag.current.startX) >= 1) update(event);
      event.stopPropagation(); stop();
    }} onPointerCancel={event => { event.stopPropagation(); if (drag.current?.id === event.pointerId) stop(); }} onLostPointerCapture={event => { event.stopPropagation(); if (drag.current?.id === event.pointerId) stop(); }} onBlur={stop} onKeyDown={event => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const step = event.shiftKey ? 10 : 1;
      const next = event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? value - step : event.key === 'ArrowRight' || event.key === 'ArrowUp' ? value + step : event.key === 'Home' ? 0 : event.key === 'End' ? 100 : null;
      if (next !== null) { event.preventDefault(); event.stopPropagation(); stop(); onChange(Math.max(0, Math.min(100, next))); }
      else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); stop(); }
      else if (event.key === ' ' || event.key === 'Enter') event.preventDefault();
    }}><span aria-hidden="true">↔</span></button>
  </>;
}
